import React from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, FormAutocomplete } from 'components/ui';
import { days, daysOfWeek, months } from 'lib/enums/dateAutocomplete';
import type { QuestionInputProps } from '../types/questionnaire';

/**
 * Shared date input component for questionnaires
 * Handles: question_date
 * Modes: default (DatePicker) or cognitive_test (Year/Month/Day/DayOfWeek)
 */
export const DateInput: React.FC<
  QuestionInputProps & { fieldName: string }
> = ({ question, value, onChange, disabled = false, fieldName }) => {
  const { t } = useTranslation();

  // Cognitive test mode: separate year, month, day, dayOfWeek fields
  if (question.mode === 'COGNITIVE_TEST') {
    return (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
        }}
      >
        <DatePicker
          name={`${fieldName}.year`}
          onChange={(date: any) =>
            onChange({ ...value, year: date?.getFullYear().toString() })
          }
          placeholder={t('Enter a year')}
          inputProps={{
            style: {
              backgroundColor: disabled ? '#f5f5f5' : 'white',
            },
            value: value?.year,
          }}
          views={['year']}
          disabled={disabled}
        />
        <FormAutocomplete
          name={`${fieldName}.month`}
          getOptionLabel={(o: any) => t(o?.label || '')}
          getOptionValue={(o: any) => o.value}
          placeholder={t('Select a month')}
          options={months}
          disabled={disabled}
        />
        <FormAutocomplete
          name={`${fieldName}.day`}
          getOptionLabel={(v: any) => (typeof v === 'number' ? v : '')}
          placeholder={t('Select a day')}
          options={days}
          disabled={disabled}
        />
        <FormAutocomplete
          name={`${fieldName}.dayOfWeek`}
          getOptionLabel={(o: any) => t(o?.label || '')}
          getOptionValue={(o: any) => o.value}
          placeholder={t('Select a day of week')}
          options={daysOfWeek}
          disabled={disabled}
        />
      </div>
    );
  }

  // Default mode: single DatePicker
  return (
    <div>
      <DatePicker
        name={fieldName}
        value={value || null}
        onChange={onChange}
        placeholder={t(
          question.includeTime ? 'Select a date and time' : 'Select a date'
        )}
        inputProps={{
          style: {
            backgroundColor: disabled ? '#f5f5f5' : 'white',
          },
        }}
        includeTime={question.includeTime}
        disabled={disabled}
      />
    </div>
  );
};
